import { useRef, useState, useEffect } from 'react';
import { Box, IconButton, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { DBUploadData, useConfig } from '../../api/api';

export const ExoCamera = ({ fileType = 'MEDIA', onImageTaken = () => {}, onError: onErrorCallback }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [state, setState] = useState('idle'); // idle | streaming | preview | uploading | error
  const [facingMode, setFacingMode] = useState('environment');
  const [capturedImage, setCapturedImage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async (mode) => {
    stopStream();
    setState('idle');
    setCapturedImage(null);
    setErrorMessage('');

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setErrorMessage(t('Camera requires a secure connection (HTTPS)'));
      setState('error');
      return;
    }

    const requestedMode = mode || facingMode;

    // Try multiple constraint strategies for maximum browser compatibility
    const constraintStrategies = [
      // 1. With facingMode as ideal preference
      { video: { facingMode: { ideal: requestedMode }, width: { ideal: 1280 }, height: { ideal: 720 } } },
      // 2. Explicit width/height only (no facingMode)
      { video: { width: { ideal: 1280 }, height: { ideal: 720 } } },
      // 3. Simplest possible constraint
      { video: true },
    ];

    let stream = null;
    let lastError = null;

    for (const constraints of constraintStrategies) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        break;
      } catch (err) {
        lastError = err;
        // NotAllowedError means user denied — no point trying other constraints
        if (err.name === 'NotAllowedError') break;
      }
    }

    if (stream) {
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setState('streaming');
      return;
    }

    const err = lastError;
    console.error('[ExoCamera] Error:', err.name, err.message);
    if (err.name === 'NotAllowedError') {
      setErrorMessage(
        t('Please allow camera access in your browser settings'),
      );
    } else if (
      err.name === 'NotFoundError' ||
      err.name === 'DevicesNotFoundError'
    ) {
      setErrorMessage(t('No Camera Found'));
    } else if (err.name === 'OverconstrainedError') {
      setErrorMessage(t('No Camera Found'));
    } else {
      setErrorMessage(t('Camera Access Denied'));
    }
    setState('error');
    onErrorCallback?.();
  };

  // Cleanup stream on unmount
  useEffect(() => {
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageDataUrl);
    stopStream();
    setState('preview');
  };

  const handleRetake = () => {
    setCapturedImage(null);
    startCamera();
  };

  const handleSwitchCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    startCamera(newMode);
  };

  const handleUsePhoto = () => {
    if (!canvasRef.current) return;
    setState('uploading');
    setProgress(0);

    canvasRef.current.toBlob(
      (blob) => {
        const formData = new FormData();
        formData.append('fileType', fileType);
        formData.append('files[]', blob, `photo_${Date.now()}.jpg`);

        DBUploadData({
          config,
          path: 'files',
          formData,
          onProgressChange: setProgress,
          onResponse: (response) => {
            onImageTaken(response);
            setState('preview');
          },
          onError: () => {
            setState('preview');
          },
        });
      },
      'image/jpeg',
      0.9,
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#000',
        aspectRatio: '3/4',
      }}
    >
      {/* Video Stream */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: state === 'streaming' || state === 'idle' ? 'block' : 'none',
        }}
      />

      {/* Hidden Canvas */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Preview */}
      {state === 'preview' && capturedImage && (
        <Box
          component="img"
          src={capturedImage}
          alt="captured"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {/* Start Camera / Loading State */}
      {state === 'idle' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            cursor: 'pointer',
          }}
          onClick={() => startCamera()}
        >
          <CameraAltIcon sx={{ fontSize: 48, color: colors.grey[300] }} />
          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
            {t('Take Photo')}
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {state === 'error' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
          }}
        >
          <NoPhotographyIcon sx={{ fontSize: 48, color: colors.error }} />
          <Typography
            variant="body2"
            sx={{
              color: colors.error,
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </Typography>
          <IconButton
            onClick={startCamera}
            sx={{
              color: colors.grey[100],
              backgroundColor: 'rgba(255,255,255,0.1)',
              mt: 1,
              px: 2,
              borderRadius: '8px',
              gap: 1,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <ReplayIcon sx={{ fontSize: 20 }} />
            <Typography variant="caption">{t('Retake')}</Typography>
          </IconButton>
        </Box>
      )}

      {/* Upload Overlay */}
      {state === 'uploading' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.6)',
            gap: 1,
          }}
        >
          <CircularProgress
            variant="determinate"
            value={progress}
            size={56}
            thickness={3}
            sx={{ color: theme.palette.primary.main }}
          />
          <Typography variant="caption" sx={{ color: '#fff' }}>
            {t('Uploading Photo')}
          </Typography>
        </Box>
      )}

      {/* Controls */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          py: 3,
          px: 2,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
        }}
      >
        {state === 'streaming' && (
          <>
            {/* Switch Camera */}
            <IconButton
              onClick={handleSwitchCamera}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                width: 44,
                height: 44,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <FlipCameraIosIcon />
            </IconButton>

            {/* Shutter Button */}
            <IconButton
              onClick={handleCapture}
              sx={{
                width: 68,
                height: 68,
                border: '3px solid #fff',
                backgroundColor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                transition: 'transform 0.15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <CameraAltIcon sx={{ color: '#fff', fontSize: 28 }} />
            </IconButton>

            {/* Spacer */}
            <Box sx={{ width: 44, height: 44 }} />
          </>
        )}

        {state === 'preview' && (
          <>
            {/* Retake */}
            <IconButton
              onClick={handleRetake}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                width: 52,
                height: 52,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <ReplayIcon sx={{ fontSize: 26 }} />
            </IconButton>

            {/* Use Photo */}
            <IconButton
              onClick={handleUsePhoto}
              sx={{
                color: '#fff',
                backgroundColor: colors.success,
                width: 60,
                height: 60,
                '&:hover': {
                  backgroundColor: colors.success,
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
};
